import React, { useState } from "react";
import { ChevronLeft, ChevronRight, LogOut, Lock, Trophy } from "lucide-react";
import { HUB_META, HUB_ORDER, GOLD } from "./constants";

export function HubSidebar({ marketUser, setupComplete, onHubClick, onLogout, adminViewAs }) {
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 1024
  );
  const activeHubMeta = marketUser
    ? (adminViewAs ? HUB_META[adminViewAs] : HUB_META[marketUser.hub])
    : null;

  return (
    <aside
      className="shrink-0 flex flex-col transition-all duration-300"
      style={{ width: collapsed ? "56px" : "340px", background: "#0C0C0E", borderRight: "1px solid rgba(255,255,255,0.07)", minHeight: "100%" }}
    >
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="self-end p-3 text-white/20 hover:text-white/60 transition-colors"
        title={collapsed ? "Expand" : "Collapse"}
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {!collapsed && (
        <div className="px-5 flex flex-col gap-3 pb-6 flex-1">
          <p className="font-head text-xs font-black uppercase tracking-[0.3em] text-white/35 mb-0.5 px-1">
            {marketUser ? "Switch Hub" : "Enter Hub"}
          </p>

          {/* Logged-in user card */}
          {marketUser && activeHubMeta && (
            <div
              className="p-4 mb-1"
              style={{ background: `rgba(${activeHubMeta.glow},0.1)`, border: `1px solid rgba(${activeHubMeta.glow},0.3)` }}
            >
              <p className="font-head text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: activeHubMeta.accent }}>
                {adminViewAs
                  ? `Admin → ${activeHubMeta.label} View`
                  : `${activeHubMeta.label} — Active`}
              </p>
              <p className="font-bold text-base text-white truncate">{marketUser.name}</p>
              {marketUser.carrier_name && (
                <p className="text-sm text-white/60 truncate">{marketUser.carrier_name}</p>
              )}
              <button
                onClick={onLogout}
                className="mt-2.5 flex items-center gap-1.5 text-white/30 hover:text-red-400 transition-colors text-xs font-head uppercase tracking-[0.12em]"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            </div>
          )}

          {/* Hub buttons */}
          <div className="space-y-2.5">
            {HUB_ORDER.map((hubKey) => {
              const m = HUB_META[hubKey];
              const HubIcon = m.icon;
              // Admin viewing another hub: highlight that hub, not the admin hub
              const isActive = adminViewAs
                ? adminViewAs === hubKey
                : marketUser?.hub === hubKey;
              return (
                <button
                  key={hubKey}
                  onClick={() => onHubClick(hubKey, setupComplete)}
                  data-testid={`sidebar-hub-${hubKey}`}
                  className="w-full flex items-center gap-3.5 px-5 py-5 text-left transition-all duration-200"
                  style={{
                    background: isActive ? `rgba(${m.glow},0.14)` : `rgba(${m.glow},0.06)`,
                    border: `1px solid rgba(${m.glow},${isActive ? "0.45" : "0.18"})`,
                    boxShadow: isActive ? `0 0 20px rgba(${m.glow},0.2), inset 0 0 20px rgba(${m.glow},0.05)` : "none",
                  }}
                >
                  <div
                    className="w-11 h-11 shrink-0 flex items-center justify-center transition-all duration-200"
                    style={{ background: `rgba(${m.glow},${isActive ? "0.22" : "0.12"})`, border: `1px solid rgba(${m.glow},${isActive ? "0.5" : "0.25"})` }}
                  >
                    <HubIcon className="w-6 h-6 transition-colors" style={{ color: m.accent, opacity: isActive ? 1 : 0.75 }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-head text-base font-black uppercase tracking-[0.08em] transition-colors" style={{ color: isActive ? m.accent : "rgba(255,255,255,0.9)" }}>
                      {m.label}
                    </p>
                    <p className="font-head text-xs truncate mt-0.5" style={{ color: isActive ? `rgba(${m.glow},0.7)` : "rgba(255,255,255,0.45)" }}>
                      {m.desc}
                    </p>
                  </div>
                  <span
                    className="font-head text-[8px] font-black uppercase tracking-[0.08em] px-2 py-1 shrink-0"
                    style={{ background: m.badgeBg, color: m.accent, border: `1px solid rgba(${m.glow},0.3)` }}
                  >
                    {m.badge}
                  </span>
                </button>
              );
            })}

            {/* Admin button */}
            <button
              onClick={() => onHubClick("admin", setupComplete)}
              data-testid="sidebar-hub-admin"
              className="w-full flex items-center gap-3.5 px-4 py-4 text-left transition-all duration-200"
              style={{ border: "1px solid rgba(167,139,250,0.22)", background: "rgba(167,139,250,0.07)" }}
            >
              <div className="w-11 h-11 shrink-0 flex items-center justify-center" style={{ background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.25)" }}>
                <Lock className="w-5 h-5" style={{ color: "#A78BFA" }} />
              </div>
              <div>
                <span className="font-head text-base font-black uppercase tracking-[0.08em]" style={{ color: "#A78BFA" }}>
                  {!setupComplete ? "Setup Required" : "Admin"}
                </span>
                <p className="text-xs text-white/35 mt-0.5">Full system access</p>
              </div>
              <span className="ml-auto font-head text-[9px] font-black uppercase px-2 py-1 shrink-0" style={{ background: "rgba(167,139,250,0.15)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.25)" }}>
                SYSTEM
              </span>
            </button>
          </div>

          {/* FREE LAUNCH badge */}
          <div className="mt-auto pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
            <div
              data-testid="hub-free-launch-badge"
              className="px-3 py-3"
              style={{ background: "rgba(245,200,66,0.1)", border: "1px solid rgba(245,200,66,0.3)" }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Trophy className="w-3.5 h-3.5" style={{ color: GOLD }} />
                <p className="font-head text-[9px] font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                  Free Launch Access
                </p>
              </div>
              <p className="text-[9px] text-white/40 leading-relaxed">
                Market access is 100% free during the loyalty promo period.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed icons only */}
      {collapsed && (
        <div className="flex flex-col items-center gap-3 px-2 py-2">
          {HUB_ORDER.map((hubKey) => {
            const m = HUB_META[hubKey];
            const HubIcon = m.icon;
            const isActive = adminViewAs
                ? adminViewAs === hubKey
                : marketUser?.hub === hubKey;
            return (
              <button
                key={hubKey}
                onClick={() => onHubClick(hubKey, setupComplete)}
                className="p-2.5 transition-colors"
                title={m.label}
                style={{
                  background: `rgba(${m.glow},${isActive ? "0.15" : "0.08"})`,
                  border: `1px solid rgba(${m.glow},${isActive ? "0.5" : "0.2"})`,
                  color: m.accent,
                  opacity: isActive ? 1 : 0.7,
                }}
              >
                <HubIcon className="w-5 h-5" />
              </button>
            );
          })}
        </div>
      )}
    </aside>
  );
}
